import { cloneElement, forwardRef } from "react";

import { type VariantProps, cva } from "class-variance-authority";
import { twMerge } from "tailwind-merge";

const DEFAULT_COLOR = "gray";
const DEFAULT_SHOW_STATUS_LIGHT = false;
const DEFAULT_IS_REMOVABLE = false;

const badge = cva(
  "inline-flex h-min w-min shrink-0 items-center rounded-full px-2 py-1 text-xs font-medium ring-1 ring-inset",
  {
    variants: {
      color: {
        plain: "bg-white text-gray-600 ring-gray-200",
        gray: "bg-gray-50 text-gray-600 ring-gray-200",
        green: "bg-green-50 text-green-700 ring-green-200",
        yellow: "bg-yellow-50 text-yellow-700 ring-yellow-200",
        red: "bg-red-50 text-red-700 ring-red-200",
        darkRed: "bg-red-100 text-red-900 ring-red-700",
        blue: "bg-blue-50 text-blue-700 ring-blue-200",
        brand: "bg-brand-50 text-brand-700 ring-brand-200",
        orange: "bg-orange-50 text-orange-700 ring-orange-200",
      },
      showStatusLight: {
        true: "gap-x-1.5",
        false: "",
      },
      isRemovable: {
        true: "gap-x-0.5",
        false: "",
      },
    },
    defaultVariants: {
      color: DEFAULT_COLOR,
      showStatusLight: DEFAULT_SHOW_STATUS_LIGHT,
      isRemovable: DEFAULT_IS_REMOVABLE,
    },
  },
);

const statusLight = cva("h-1.5 w-1.5 overflow-visible", {
  variants: {
    color: {
      plain: "fill-gray-400",
      gray: "fill-gray-400",
      blue: "fill-blue-500",
      green: "fill-green-500",
      yellow: "fill-yellow-500",
      red: "fill-red-500",
      darkRed: "fill-red-700",
      brand: "fill-brand-500",
      orange: "fill-orange-500",
    },
  },
});

const removeButton = cva("group relative -mr-1 h-3.5 w-3.5 rounded-sm", {
  variants: {
    color: {
      plain: "hover:bg-gray-400/20",
      gray: "hover:bg-gray-500/20",
      blue: "hover:bg-blue-600/20",
      green: "hover:bg-green-600/20",
      yellow: "hover:bg-yellow-600/20",
      red: "hover:bg-red-600/20",
      darkRed: "hover:bg-red-800/20",
      brand: "hover:bg-brand-600/20",
      orange: "hover:bg-orange-600/20",
    },
  },
});

const removeButtonIcon = cva("h-3.5 w-3.5", {
  variants: {
    color: {
      plain: "stroke-gray-600/50 group-hover:stroke-gray-600/75",
      gray: "stroke-gray-700/50 group-hover:stroke-gray-700/75",
      blue: "stroke-blue-800/50 group-hover:stroke-blue-800/75",
      green: "stroke-green-800/50 group-hover:stroke-green-800/75",
      yellow: "stroke-yellow-800/50 group-hover:stroke-yellow-800/75",
      red: "stroke-red-700/50 group-hover:stroke-red-700/75",
      darkRed: "stroke-red-900/50 group-hover:stroke-red-900/75",
      brand: "stroke-brand-700/50 group-hover:stroke-brand-700/75",
      orange: "stroke-orange-700/50 group-hover:stroke-orange-700/75",
    },
  },
});

type BadgeVariantProps = VariantProps<typeof badge>;

export interface Props extends BadgeVariantProps {
  label: string;
  showStatusLight?: boolean;
  startIcon?: React.ReactElement<{
    className?: string;
  }> | null;
  endIcon?: React.ReactElement<{
    className?: string;
  }> | null;
  isRemovable?: boolean;
  className?: string;
  classNames?: {
    label?: string;
    statusLight?: string;
    removeButton?: string;
    removeButtonIcon?: string;
  };
  onRemove?: () => void;
  onClick?: (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => void;
}

const Badge = forwardRef<HTMLSpanElement, Props>(
  (
    {
      label,
      color = DEFAULT_COLOR,
      showStatusLight = DEFAULT_SHOW_STATUS_LIGHT,
      startIcon,
      endIcon,
      isRemovable = DEFAULT_IS_REMOVABLE,
      className,
      classNames,
      onRemove = () => {},
      onClick = () => {},
      ...rest
    },
    ref,
  ) => {
    const styledStartIcon = startIcon
      ? cloneElement(startIcon, {
          className: twMerge("mr-1.5 size-4", startIcon.props.className),
        })
      : null;
    const styledEndIcon = endIcon
      ? cloneElement(endIcon, {
          className: twMerge("ml-1.5 size-4", endIcon.props.className),
        })
      : null;

    return (
      <span
        className={twMerge(
          badge({
            color,
            showStatusLight,
            isRemovable,
            className,
          }),
        )}
        onClick={onClick}
        ref={ref}
        {...rest}
      >
        {showStatusLight && (
          <svg
            className={twMerge(
              statusLight({ color, className: classNames?.statusLight }),
            )}
            viewBox="0 0 6 6"
          >
            <circle cx={3} cy={3} r={3} />
          </svg>
        )}
        {styledStartIcon && styledStartIcon}
        <span className={twMerge("whitespace-nowrap", classNames?.label)}>
          {label}
        </span>
        {styledEndIcon && styledEndIcon}
        {isRemovable && (
          <button
            type="button"
            className={twMerge(
              removeButton({ color, className: classNames?.removeButton }),
            )}
            onClick={() => {
              onRemove?.();
            }}
          >
            <svg
              viewBox="0 0 14 14"
              className={twMerge(
                removeButtonIcon({
                  color,
                  className: classNames?.removeButtonIcon,
                }),
              )}
            >
              <path d="M4 4l6 6m0-6l-6 6" />
            </svg>
          </button>
        )}
      </span>
    );
  },
);

Badge.displayName = "Badge";

export default Badge;
